import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';

// Input with validation
export const ValidatedInput: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
  validate?: (value: string) => string | null;
  showValidationIcon?: boolean;
  className?: string;
  disabled?: boolean;
  autoComplete?: string;
}> = ({
  id,
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  minLength,
  maxLength,
  pattern,
  patternMessage,
  validate,
  showValidationIcon = true,
  className = '',
  disabled = false,
  autoComplete
}) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  
  // Determine actual input type for password fields
  const actualType = type === 'password' && passwordVisible ? 'text' : type;

  // Validate on value change, but only if the field has been touched
  useEffect(() => {
    if (!touched) return;
    
    setIsValidating(true);
    
    // Use a small delay to simulate validation and avoid UI flicker
    const timer = setTimeout(() => {
      let errorMessage: string | null = null;
      
      if (required && !value) {
        errorMessage = `${label} is required`;
      } else if (minLength && value.length < minLength) {
        errorMessage = `${label} must be at least ${minLength} characters`;
      } else if (maxLength && value.length > maxLength) {
        errorMessage = `${label} must be less than ${maxLength} characters`;
      } else if (pattern && !new RegExp(pattern).test(value) && value) {
        errorMessage = patternMessage || `${label} format is invalid`;
      } else if (validate && value) {
        errorMessage = validate(value);
      }
      
      setError(errorMessage);
      setIsValidating(false);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [value, touched, required, minLength, maxLength, pattern, patternMessage, validate, label]);

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          id={id}
          type={actualType}
          value={value}
          onChange={onChange}
          onBlur={() => setTouched(true)}
          placeholder={placeholder}
          className={`input-base pr-10 ${error ? 'input-error' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
          disabled={disabled}
          autoComplete={autoComplete}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        
        {/* Show password toggle for password fields */}
        {type === 'password' && (
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            onClick={() => setPasswordVisible(!passwordVisible)}
            tabIndex={-1}
          >
            {passwordVisible ? <EyeOff size={18} /> : <Eye size={18} />}
          </button>
        )}
        
        {/* Validation icon */}
        {showValidationIcon && touched && value && !isValidating && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            {error ? (
              <AlertCircle className="text-red-500" size={18} />
            ) : (
              <CheckCircle className="text-green-500" size={18} />
            )}
          </div>
        )}
      </div>
      
      {/* Error message */}
      {error && touched && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      
      {/* Character count for inputs with maxLength */}
      {maxLength && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  );
};

// Textarea with validation
export const ValidatedTextarea: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  rows?: number;
  validate?: (value: string) => string | null;
  className?: string;
  disabled?: boolean;
}> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  required = false,
  minLength,
  maxLength,
  rows = 4,
  validate,
  className = '',
  disabled = false
}) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  
  // Validate on value change, but only if the field has been touched
  useEffect(() => {
    if (!touched) return;
    
    let errorMessage: string | null = null;
    
    if (required && !value) {
      errorMessage = `${label} is required`;
    } else if (minLength && value.length < minLength) {
      errorMessage = `${label} must be at least ${minLength} characters`;
    } else if (maxLength && value.length > maxLength) {
      errorMessage = `${label} must be less than ${maxLength} characters`;
    } else if (validate) {
      errorMessage = validate(value);
    }
    
    setError(errorMessage);
  }, [value, touched, required, minLength, maxLength, validate, label]);

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <textarea
        id={id}
        value={value}
        onChange={onChange}
        onBlur={() => setTouched(true)}
        placeholder={placeholder}
        rows={rows}
        className={`textarea-base ${error ? 'input-error' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={disabled}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      
      {/* Error message */}
      {error && touched && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
      
      {/* Character count for textareas with maxLength */}
      {maxLength && (
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 text-right">
          {value.length}/{maxLength}
        </div>
      )}
    </div>
  );
};

// Select with validation
export const ValidatedSelect: React.FC<{
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  validate?: (value: string) => string | null;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
}> = ({
  id,
  label,
  value,
  onChange,
  options,
  required = false,
  validate,
  className = '',
  disabled = false,
  placeholder
}) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  
  // Validate on value change, but only if the field has been touched
  useEffect(() => {
    if (!touched) return;
    
    let errorMessage: string | null = null;
    
    if (required && !value) {
      errorMessage = `${label} is required`;
    } else if (validate) {
      errorMessage = validate(value);
    }
    
    setError(errorMessage);
  }, [value, touched, required, validate, label]);

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <select
        id={id}
        value={value}
        onChange={onChange}
        onBlur={() => setTouched(true)}
        className={`select-base ${error ? 'input-error' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        disabled={disabled}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${id}-error` : undefined}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      
      {/* Error message */}
      {error && touched && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

// Checkbox with validation
export const ValidatedCheckbox: React.FC<{
  id: string;
  label: React.ReactNode;
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  validate?: (checked: boolean) => string | null;
  className?: string;
  disabled?: boolean;
}> = ({
  id,
  label,
  checked,
  onChange,
  required = false,
  validate,
  className = '',
  disabled = false
}) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  
  // Validate on value change, but only if the field has been touched
  useEffect(() => {
    if (!touched) return;
    
    let errorMessage: string | null = null;
    
    if (required && !checked) {
      errorMessage = 'This field is required';
    } else if (validate) {
      errorMessage = validate(checked);
    }
    
    setError(errorMessage);
  }, [checked, touched, required, validate]);

  return (
    <div className={className}>
      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={onChange}
            onBlur={() => setTouched(true)}
            className={`w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 rounded focus:ring-green-500 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            disabled={disabled}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? `${id}-error` : undefined}
          />
        </div>
        <div className="ml-3 text-sm">
          <label htmlFor={id} className={`font-medium ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
            {label} {required && <span className="text-red-500">*</span>}
          </label>
        </div>
      </div>
      
      {/* Error message */}
      {error && touched && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

// Radio Group with validation
export const ValidatedRadioGroup: React.FC<{
  id: string;
  legend: string;
  options: Array<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  validate?: (value: string) => string | null;
  className?: string;
  disabled?: boolean;
  inline?: boolean;
}> = ({
  id,
  legend,
  options,
  value,
  onChange,
  required = false,
  validate,
  className = '',
  disabled = false,
  inline = false
}) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  
  // Validate on value change, but only if the field has been touched
  useEffect(() => {
    if (!touched) return;
    
    let errorMessage: string | null = null;
    
    if (required && !value) {
      errorMessage = `${legend} is required`;
    } else if (validate) {
      errorMessage = validate(value);
    }
    
    setError(errorMessage);
  }, [value, touched, required, validate, legend]);

  return (
    <div className={className}>
      <fieldset>
        <legend className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {legend} {required && <span className="text-red-500">*</span>}
        </legend>
        <div className={`${inline ? 'flex flex-wrap gap-4' : 'space-y-2'}`}>
          {options.map(option => (
            <div key={option.value} className="flex items-center">
              <input
                id={`${id}-${option.value}`}
                name={id}
                type="radio"
                value={option.value}
                checked={value === option.value}
                onChange={() => {
                  onChange(option.value);
                  setTouched(true);
                }}
                className={`w-4 h-4 text-green-600 border-gray-300 dark:border-gray-600 focus:ring-green-500 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
                disabled={disabled}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${id}-error` : undefined}
              />
              <label 
                htmlFor={`${id}-${option.value}`} 
                className={`ml-3 block text-sm ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}
              >
                {option.label}
              </label>
            </div>
          ))}
        </div>
      </fieldset>
      
      {/* Error message */}
      {error && touched && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

// File Input with validation
export const ValidatedFileInput: React.FC<{
  id: string;
  label: string;
  onChange: (file: File | null) => void;
  accept?: string;
  required?: boolean;
  maxSize?: number; // in MB
  validate?: (file: File | null) => string | null;
  className?: string;
  disabled?: boolean;
  showPreview?: boolean;
}> = ({
  id,
  label,
  onChange,
  accept,
  required = false,
  maxSize,
  validate,
  className = '',
  disabled = false,
  showPreview = false
}) => {
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  
  // Clean up preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    setFile(selectedFile);
    setTouched(true);
    
    // Generate preview for images
    if (selectedFile && showPreview && selectedFile.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(selectedFile);
      setPreview(previewUrl);
    } else {
      setPreview(null);
    }
    
    // Validate file
    let errorMessage: string | null = null;
    
    if (required && !selectedFile) {
      errorMessage = `${label} is required`;
    } else if (selectedFile) {
      if (maxSize && selectedFile.size > maxSize * 1024 * 1024) {
        errorMessage = `File size must be less than ${maxSize}MB`;
      } else if (validate) {
        errorMessage = validate(selectedFile);
      }
    }
    
    setError(errorMessage);
    onChange(errorMessage ? null : selectedFile);
  };

  return (
    <div className={className}>
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-lg hover:border-gray-400 dark:hover:border-gray-500 transition-colors">
        <div className="space-y-1 text-center">
          <svg className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <div className="flex text-sm text-gray-600 dark:text-gray-400">
            <label
              htmlFor={id}
              className={`relative cursor-pointer rounded-md font-medium text-green-600 dark:text-green-400 hover:text-green-500 dark:hover:text-green-300 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-green-500 ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <span>Upload a file</span>
              <input
                id={id}
                name={id}
                type="file"
                className="sr-only"
                onChange={handleFileChange}
                accept={accept}
                disabled={disabled}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${id}-error` : undefined}
              />
            </label>
            <p className="pl-1">or drag and drop</p>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {accept ? `${accept.split(',').join(', ')} up to ${maxSize}MB` : 'Any file format'}
          </p>
          {file && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Selected: {file.name} ({(file.size / 1024 / 1024).toFixed(2)}MB)
            </p>
          )}
        </div>
      </div>
      
      {/* Preview for images */}
      {preview && (
        <div className="mt-3">
          <img src={preview} alt="Preview" className="h-32 object-contain rounded-lg" />
        </div>
      )}
      
      {/* Error message */}
      {error && touched && (
        <p id={`${id}-error`} className="mt-1 text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
};

// Form with validation
export const ValidatedForm: React.FC<{
  onSubmit: (e: React.FormEvent) => void;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
}> = ({ onSubmit, children, className = '', disabled = false, loading = false }) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!disabled && !loading) {
      onSubmit(e);
    }
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      className={`${className} ${disabled || loading ? 'opacity-70 pointer-events-none' : ''}`}
      noValidate
    >
      {loading && (
        <div className="fixed inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-lg flex items-center space-x-3">
            <div className="w-6 h-6 border-2 border-green-600 dark:border-green-400 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-700 dark:text-gray-300">Processing...</p>
          </div>
        </div>
      )}
      {children}
    </form>
  );
};